import { Col } from 'antd';
import { useFormikContext } from 'formik';
import { isNil } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUnit } from '../../../../../api/types';
import RadioGroupField from '../../../../../components/fields/RadioGroupField';
import SelectField from '../../../../../components/fields/SelectField';
import FormFields from '../../../../../themes/admin/Fields/FormFields';
import { useFinanceProductDetailContext } from '../../FinanceProductDetailContext';
import { CommonType } from '../../shared/typings';
import useFinanceProductOptions from '../../shared/useFinanceProductOptions';
import { type FinanceProductFormValues } from '../index';
import DefaultIntervalField from './DefaultIntervalField';

const colSpan = { lg: 8, xs: 24 };

const DownPayment = ({ disabled = false }: { disabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { bank } = useFinanceProductDetailContext();

    const { commonTypeOptions, amountUnitOptions } = useFinanceProductOptions(bank?.module?.companyId);
    const { values } = useFormikContext<FinanceProductFormValues>();

    const { downPayment } = values;

    const { disableStep, disableUnit } = useMemo(() => {
        const minUnit = downPayment?.minUnit;
        const maxUnit = downPayment?.maxUnit;
        const stepUnit = downPayment?.stepUnit;
        const step = downPayment?.step;

        const common = !isNil(minUnit) && !isNil(maxUnit);

        return {
            disableStep: common && minUnit !== maxUnit && Number.isFinite(step),
            disableUnit:
                common &&
                (minUnit !== maxUnit || (minUnit === AmountUnit.Currency && maxUnit === AmountUnit.Currency)) &&
                !isNil(stepUnit),
        };
    }, [downPayment?.maxUnit, downPayment?.minUnit, downPayment?.step, downPayment?.stepUnit]);

    return (
        <>
            <Col span={24}>
                <RadioGroupField
                    {...t('financeProductDetails:fields.downPaymentType', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="downPayment.type"
                    options={commonTypeOptions}
                    required
                />
            </Col>
            {downPayment?.type === CommonType.Range && (
                <>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.downPaymentMin', {
                                returnObjects: true,
                            })}
                            addonAfter={
                                <SelectField
                                    itemProps={{ noStyle: true }}
                                    name="downPayment.minUnit"
                                    options={amountUnitOptions}
                                />
                            }
                            disabled={disabled}
                            max={downPayment?.minUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="downPayment.min"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.downPaymentMax', {
                                returnObjects: true,
                            })}
                            addonAfter={
                                <SelectField
                                    itemProps={{ noStyle: true }}
                                    name="downPayment.maxUnit"
                                    options={amountUnitOptions}
                                />
                            }
                            disabled={disabled}
                            max={downPayment?.maxUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="downPayment.max"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.downPaymentStep', {
                                returnObjects: true,
                            })}
                            addonAfter={
                                <SelectField
                                    disabled={disableUnit}
                                    itemProps={{ noStyle: true }}
                                    name="downPayment.stepUnit"
                                    options={amountUnitOptions}
                                />
                            }
                            disabled={disableStep || disabled}
                            max={downPayment?.stepUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="downPayment.step"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <DefaultIntervalField
                            {...t('financeProductDetails:fields.downPaymentDefault', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            max={downPayment?.unit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="downPayment.default"
                            unitProps={{ name: 'downPayment.unit' }}
                            required
                        />
                    </Col>
                </>
            )}
        </>
    );
};

export default DownPayment;
